<!-- eslint-disable -->
<template>
  <transition name="crm-drawer-slide">
    <div
      v-if="card"
      class="crm-drawer-backdrop"
      @mousedown.self="$emit('close')"
    >
      <div class="crm-drawer">
        <!-- Header -->
        <div class="crm-drawer__header">
          <div class="crm-drawer__header-left">
            <!-- Karim, 8 Sep: "change the ID of it to the request ID" once
                 a Won opportunity has a linked ERP application/request. -->
            <span class="crm-drawer__id" :title="card.requestCode ? `Internal id: ${card.id}` : null">
              {{ card.requestCode || card.id }}
            </span>
            <!-- Inline editable title -->
            <div class="crm-inline-edit crm-drawer__title-wrap">
              <h2
                v-if="!editingTitle"
                class="crm-drawer__title"
                @click="startEditTitle"
              >{{ card.title }}</h2>
              <input
                v-else
                ref="titleInput"
                type="text"
                class="crm-inline-edit__input crm-inline-edit__input--title"
                v-model="editTitle"
                @blur="saveTitle"
                @keyup.enter="saveTitle"
                @keyup.escape="editingTitle = false"
              />
            </div>
          </div>
          <button class="crm-drawer__close" @click="$emit('close')">
            <feather-icon icon="XIcon" size="18" />
          </button>
        </div>

        <!-- Scrollable body -->
        <div class="crm-drawer__body">

          <!-- 1. Stage Selector -->
          <div class="crm-drawer__section">
            <label class="crm-drawer__section-label">
              <feather-icon icon="GitBranchIcon" size="14" />
              Stage
            </label>
            <select
              class="crm-select"
              :value="card.stage"
              @change="$emit('stage-change', { card, newStage: $event.target.value })"
            >
              <option
                v-for="stage in stages"
                :key="stage.id"
                :value="stage.id"
              >{{ stage.name }}</option>
            </select>
          </div>

          <!-- 2. General Info Grid -->
          <div class="crm-drawer__section crm-info-grid">
            <!-- Client -->
            <div class="crm-info-grid__item">
              <label class="crm-info-grid__label">Client / Facility</label>
              <div class="crm-inline-edit">
                <span
                  v-if="!editingClient"
                  class="crm-inline-edit__value"
                  @click="startEdit('client')"
                >{{ card.client || 'Click to add…' }}</span>
                <input
                  v-else
                  ref="clientInput"
                  type="text"
                  class="crm-inline-edit__input"
                  v-model="editClient"
                  @blur="saveField('client', editClient)"
                  @keyup.enter="saveField('client', editClient)"
                  @keyup.escape="editingClient = false"
                />
              </div>
            </div>

            <!-- Value -->
            <div class="crm-info-grid__item" v-if="activePipeline !== 'tickets'">
              <label class="crm-info-grid__label">Value (AED)</label>
              <div class="crm-inline-edit">
                <span
                  v-if="!editingValue"
                  class="crm-inline-edit__value crm-inline-edit__value--money"
                  @click="startEdit('value')"
                >{{ formatCurrency(card.value) }}</span>
                <input
                  v-else
                  ref="valueInput"
                  type="number"
                  class="crm-inline-edit__input"
                  v-model.number="editValue"
                  @blur="saveField('value', editValue)"
                  @keyup.enter="saveField('value', editValue)"
                  @keyup.escape="editingValue = false"
                />
              </div>
            </div>

            <!-- Priority -->
            <div class="crm-info-grid__item">
              <label class="crm-info-grid__label">Priority</label>
              <div class="crm-inline-edit">
                <span
                  v-if="!editingPriority"
                  class="crm-inline-edit__value"
                  @click="editingPriority = true"
                >
                  <span class="crm-priority-dot" :style="{ backgroundColor: priorityColor(card.priority) }"></span>
                  {{ card.priority }}
                </span>
                <select
                  v-else
                  class="crm-inline-edit__input"
                  :value="card.priority"
                  @change="saveField('priority', $event.target.value); editingPriority = false"
                  @blur="editingPriority = false"
                  ref="priorityInput"
                >
                  <option value="High">High</option>
                  <option value="Medium">Medium</option>
                  <option value="Low">Low</option>
                </select>
              </div>
            </div>

            <!--
              Karim, 8 Sep: "when adding or editing opportunity, I can select
              the product any time and it automatically adds the value. And
              auto select 'quotation'." Adding already had this (CardAddModal);
              editing an existing opportunity had no way to change the product
              at all until now.
            -->
            <div class="crm-info-grid__item" v-if="activePipeline !== 'tickets'">
              <label class="crm-info-grid__label">Product / Service</label>
              <div class="crm-inline-edit">
                <span
                  v-if="!editingProduct"
                  class="crm-inline-edit__value"
                  @click="startEditProduct"
                >{{ card.serviceScheme || 'Select a product…' }}</span>
                <div v-else class="d-flex flex-column" style="gap: 4px; min-width: 220px">
                  <select class="crm-input crm-input--sm" v-model="productSchemeEdit" @change="onProductSchemeEditChange">
                    <option value="">— scheme —</option>
                    <option v-for="s in schemeOptions" :key="s" :value="s">{{ s }}</option>
                  </select>
                  <select
                    v-if="productSchemeEdit"
                    class="crm-input crm-input--sm"
                    v-model="productIdEdit"
                    @change="applyProductEdit"
                  >
                    <option value="">— product / regulation —</option>
                    <option v-for="p in productsForSchemeEdit" :key="p.id" :value="p.id">{{ p.regulation || p.crmName }}</option>
                  </select>
                  <small v-if="productPriceLoading" class="text-muted">Fetching price…</small>
                  <button type="button" class="crm-icon-btn" style="align-self:flex-end" @click="editingProduct = false">
                    <feather-icon icon="XIcon" size="14" />
                  </button>
                </div>
              </div>
            </div>

            <!-- Owner -->
            <div class="crm-info-grid__item">
              <label class="crm-info-grid__label">Owner</label>
              <span class="crm-info-grid__static">{{ card.owner || '—' }}</span>
            </div>

            <!-- Remaining Days or Created -->
            <div class="crm-info-grid__item" v-if="activePipeline === 'renewals'">
              <label class="crm-info-grid__label">Expiry Days</label>
              <span class="crm-info-grid__static" :class="{'crm-text-danger': card.remainingDays <= 7}">
                {{ card.remainingDays }} days
              </span>
            </div>
            <div class="crm-info-grid__item" v-else>
              <label class="crm-info-grid__label">Created</label>
              <span class="crm-info-grid__static">{{ card.createdAt || '—' }}</span>
            </div>

            <!-- Due Date -->
            <div class="crm-info-grid__item">
              <label class="crm-info-grid__label">Due Date</label>
              <flat-pickr
                :value="card.dueDate"
                class="crm-inline-edit__input crm-inline-edit__input--date"
                :config="dateConfig"
                placeholder="Set due date…"
                @on-change="onDueDateChange"
              />
            </div>
          </div>

          <!--
            Karim, 27 Sep: the data needed to renew — certificate number,
            brand, model, issue/expiry date, product category. Brand / model /
            category exist in the ERP only where the request has products
            (Saber today); Jeem and the rest show "—".
          -->
          <div v-if="activePipeline === 'renewals'" class="crm-drawer__section">
            <label class="crm-drawer__section-label">
              <feather-icon icon="AwardIcon" size="14" />
              Certificate being renewed
            </label>
            <p v-if="renewingCert.loading" class="text-muted mb-0" style="font-size: 12.5px">Loading…</p>
            <p v-else-if="!renewingCert.data" class="text-muted mb-0" style="font-size: 12.5px">
              {{ renewingCert.error || 'Not linked to an ERP certificate.' }}
            </p>
            <div v-else class="crm-info-grid crm-renew-cert">
              <div class="crm-info-grid__item">
                <label class="crm-info-grid__label">Request to renew</label>
                <a
                  v-if="renewingCert.data.requestId"
                  class="crm-info-grid__static crm-renew-cert__link"
                  :href="requestHref(renewingCert.data.requestId)"
                  target="_blank"
                  rel="noopener"
                >{{ renewingCert.data.requestCode || renewingCert.data.requestId }}
                  <feather-icon icon="ExternalLinkIcon" size="12" />
                </a>
                <span v-else class="crm-info-grid__static">—</span>
              </div>
              <div class="crm-info-grid__item">
                <label class="crm-info-grid__label">Product</label>
                <span class="crm-info-grid__static" :title="renewingList('products', 50)">{{ renewingList('products') || '—' }}</span>
              </div>
              <div class="crm-info-grid__item">
                <label class="crm-info-grid__label">Certificate Number</label>
                <span class="crm-info-grid__static">{{ renewingCert.data.certificateNumber || '—' }}</span>
              </div>
              <div class="crm-info-grid__item">
                <label class="crm-info-grid__label">Brand</label>
                <span v-if="renewingList('brands')" class="crm-info-grid__static" :title="renewingList('brands', 50)">{{ renewingList('brands') }}</span>
                <span v-else-if="renewingList('manufacturers')" class="crm-info-grid__static" :title="renewingList('manufacturers', 50)">{{ renewingList('manufacturers') }} <small class="text-muted">(manufacturer)</small></span>
                <span v-else class="crm-info-grid__static">—</span>
              </div>
              <div class="crm-info-grid__item">
                <label class="crm-info-grid__label">Model</label>
                <span class="crm-info-grid__static" :title="renewingList('models', 50)">{{ renewingList('models') || '—' }}</span>
              </div>
              <div class="crm-info-grid__item">
                <label class="crm-info-grid__label">Product Category</label>
                <span class="crm-info-grid__static" :title="renewingCert.data.productCategory">{{ shortText(renewingCert.data.productCategory) || '—' }}</span>
              </div>
              <div class="crm-info-grid__item">
                <label class="crm-info-grid__label">Issue Date</label>
                <span class="crm-info-grid__static">{{ renewingCert.data.issuedAt || '—' }}</span>
              </div>
              <div class="crm-info-grid__item">
                <label class="crm-info-grid__label">Expiry Date</label>
                <span class="crm-info-grid__static">{{ renewingCert.data.expiresAt || '—' }}</span>
              </div>
            </div>
          </div>

          <!--
            3. Salesperson (the OWNER)
            MID-CRM P1.3 / P2.1: this used to be a multi-"Assignees" list. There is no
            crm_card_assignees table, so every name added there was silently dropped by
            the server, and the picker was fed MOCK_TEAM_MEMBERS (Mohammed, Sara…) that
            matched no real user. A card has exactly ONE owner (`owner_id`) — which is
            also all the BRD models (one Salesperson per deal) — so this is now a single
            owner picker backed by GET crm/team, and it really persists.
          -->
          <div class="crm-drawer__section">
            <label class="crm-drawer__section-label">
              <feather-icon icon="UserIcon" size="14" />
              Salesperson
            </label>
            <div class="crm-assignees">
              <div class="crm-assignee" v-if="card.owner">
                <span class="crm-assignee__avatar">{{ getInitials(card.owner) }}</span>
                <span class="crm-assignee__name">{{ card.owner }}</span>
              </div>
              <span v-else class="text-xs text-muted">Nobody is responsible for this opportunity yet.</span>

              <!-- Only a manager may hand work to someone else (BRD §8.1). -->
              <template v-if="canAssign">
                <div class="crm-assignee-add" v-if="!showAssigneeSelect">
                  <button class="crm-link-btn" @click="showAssigneeSelect = true">
                    <feather-icon icon="RepeatIcon" size="13" />
                    {{ card.owner ? 'Change' : 'Assign' }}
                  </button>
                </div>
                <div class="crm-assignee-select" v-else>
                  <select
                    class="crm-input crm-input--sm"
                    :value="card.ownerId || ''"
                    @change="setOwner($event.target.value)"
                  >
                    <option value="">— select a salesperson —</option>
                    <option v-for="u in team" :key="u.id" :value="u.id">{{ memberLabel(u) }}</option>
                  </select>
                  <button class="crm-icon-btn" @click="showAssigneeSelect = false">
                    <feather-icon icon="XIcon" size="14" />
                  </button>
                </div>
                <small v-if="!team.length" class="text-danger d-block mt-1">
                  No salespeople loaded — the team list could not be fetched.
                </small>
              </template>
            </div>
          </div>

          <!--
            Karim, 7 Sep: "opportunity can have multiple assign of sales" —
            fully equal co-owners, no primary. This is on TOP of the single
            Salesperson/owner above (kept as-is for targets/reporting) — real
            table this time (crm_card_assignees), not the removed mock.
          -->
          <div class="crm-drawer__section">
            <label class="crm-drawer__section-label">
              <feather-icon icon="UsersIcon" size="14" />
              Other Assignees
            </label>
            <div class="d-flex flex-wrap align-items-center" style="gap: 6px">
              <span
                v-for="a in otherAssignees"
                :key="a.id"
                class="crm-assignee-chip"
              >
                {{ a.name || `User #${a.id}` }}
                <button type="button" class="crm-assignee-chip__remove" @click="removeAssignee(a)">
                  <feather-icon icon="XIcon" size="10" />
                </button>
              </span>
              <span v-if="!otherAssignees.length" class="text-xs text-muted">Nobody else assigned yet.</span>

              <div class="crm-assignee-add" v-if="!showAddAssignee">
                <button class="crm-link-btn" @click="showAddAssignee = true">
                  <feather-icon icon="UserPlusIcon" size="13" />
                  Add
                </button>
              </div>
              <div class="crm-assignee-select" v-else>
                <select class="crm-input crm-input--sm" v-model="assigneeToAdd">
                  <option value="">— select —</option>
                  <option v-for="u in team" :key="u.id" :value="u.id">{{ memberLabel(u) }}</option>
                </select>
                <button class="crm-icon-btn" :disabled="!assigneeToAdd" @click="addAssignee">
                  <feather-icon icon="CheckIcon" size="14" />
                </button>
                <button class="crm-icon-btn" @click="showAddAssignee = false; assigneeToAdd = ''">
                  <feather-icon icon="XIcon" size="14" />
                </button>
              </div>
            </div>
          </div>

          <!-- 4. Labels -->
          <div class="crm-drawer__section">
            <label class="crm-drawer__section-label">
              <feather-icon icon="TagIcon" size="14" />
              Labels
            </label>
            <div class="crm-labels">
              <span
                class="crm-label-pill"
                v-for="(label, i) in (card.labels || [])"
                :key="i"
                :style="{ backgroundColor: label.color + '18', color: label.color, borderColor: label.color + '30' }"
              >
                {{ label.name }}
                <button class="crm-label-pill__remove" @click="$emit('label-remove', { cardId: card.id, labelIndex: i })">
                  <feather-icon icon="XIcon" size="10" />
                </button>
              </span>
              <div class="crm-label-add" v-if="!showLabelSelect">
                <button class="crm-link-btn" @click="showLabelSelect = true">
                  <feather-icon icon="PlusIcon" size="13" />
                  Add label
                </button>
              </div>
              <div class="crm-label-select" v-if="showLabelSelect">
                <v-select
                  :options="availableLabels"
                  label="name"
                  placeholder="Select label…"
                  class="crm-vselect crm-vselect--sm"
                  @input="onLabelSelect"
                >
                  <template #option="{ name, color }">
                    <span class="crm-label-dot" :style="{ backgroundColor: color }"></span>
                    {{ name }}
                  </template>
                </v-select>
                <button class="crm-icon-btn" @click="showLabelSelect = false">
                  <feather-icon icon="XIcon" size="14" />
                </button>
              </div>
            </div>
          </div>

          <!-- 5. Description -->
          <div class="crm-drawer__section">
            <label class="crm-drawer__section-label">
              <feather-icon icon="AlignLeftIcon" size="14" />
              Description
            </label>
            <div v-if="!editingDesc" class="crm-desc-display" @click="startEditDesc">
              <p v-if="card.description">{{ card.description }}</p>
              <p v-else class="crm-desc-display__placeholder">Click to add description…</p>
            </div>
            <div v-else class="crm-desc-edit">
              <textarea
                ref="descTextarea"
                class="crm-textarea"
                v-model="editDesc"
                rows="4"
              ></textarea>
              <div class="crm-desc-edit__actions">
                <button class="crm-btn crm-btn--primary crm-btn--sm" @click="saveDesc">Save</button>
                <button class="crm-btn crm-btn--ghost crm-btn--sm" @click="editingDesc = false">Cancel</button>
              </div>
            </div>
          </div>

          <!-- 6. Tabs -->
          <div class="crm-drawer__section crm-drawer__tabs-section">
            <div class="crm-tabs">
              <button
                class="crm-tab"
                :class="{ 'crm-tab--active': activeTab === 'client' }"
                @click="activeTab = 'client'"
              >
                <feather-icon icon="UserIcon" size="13" />
                Client
              </button>
              <button
                class="crm-tab"
                :class="{ 'crm-tab--active': activeTab === 'comments' }"
                @click="activeTab = 'comments'"
              >
                <feather-icon icon="MessageSquareIcon" size="13" />
                Comments
                <span class="crm-tab__count" v-if="(card.comments || []).length">{{ (card.comments || []).length }}</span>
              </button>
              <!-- MID-CRM P1.5: the Checklist and Time Log tabs are GONE, not just
                   hidden — nothing stored what they collected. See the note where
                   their panels used to be. -->
              <button
                class="crm-tab"
                :class="{ 'crm-tab--active': activeTab === 'activity' }"
                @click="activeTab = 'activity'"
              >
                <feather-icon icon="ActivityIcon" size="13" />
                Activity
              </button>
              <button
                v-if="activePipeline !== 'tickets'"
                class="crm-tab"
                :class="{ 'crm-tab--active': activeTab === 'quotation' }"
                @click="activeTab = 'quotation'"
              >
                <feather-icon icon="FileTextIcon" size="13" />
                Quotation
              </button>
              <!--
                Karim, 7 Sep: real attachments this time — spatie/laravel-
                medialibrary behind it (see CrmCard::getAttachmentsAttribute()),
                replacing the mock version removed above.
              -->
              <button
                class="crm-tab"
                :class="{ 'crm-tab--active': activeTab === 'files' }"
                @click="activeTab = 'files'"
              >
                <feather-icon icon="PaperclipIcon" size="13" />
                Files
                <span class="crm-tab__count" v-if="(card.attachments || []).length">{{ (card.attachments || []).length }}</span>
              </button>
            </div>

            <!-- Client Tab — the full client record inside the opportunity -->
            <div class="crm-tab-panel" v-if="activeTab === 'client'">
              <client-panel :card="card" />

              <!--
                Audit C-1: the certificate number closes the loop with the ERP.
                The number the rep types is matched against the certificates that
                came from the ERP — only a REAL match flips the card to Won.
                An unmatched number is stored as `unverified` and never fakes a win.
              -->
              <div class="crm-cert-box">
                <div class="crm-drawer__subhead">
                  <feather-icon icon="AwardIcon" size="13" />
                  <span>Certificate (closes this opportunity)</span>
                </div>

                <div class="crm-cert-box__row">
                  <input
                    v-model="certNumber"
                    type="text"
                    class="crm-input crm-input--sm"
                    placeholder="Jeem / Saber / SFDA certificate number"
                    :disabled="savingCert"
                    @keyup.enter="linkCertificate"
                  />
                  <button
                    class="crm-btn crm-btn--primary crm-btn--sm"
                    :disabled="savingCert || !certNumber"
                    @click="linkCertificate"
                  >
                    {{ savingCert ? 'Checking…' : 'Verify' }}
                  </button>
                </div>

                <!-- Status is reported honestly, never dressed up as a win -->
                <div v-if="certStatus" class="crm-cert-box__status" :class="certStatusClass">
                  <feather-icon :icon="certStatusIcon" size="13" />
                  <span>{{ certStatusText }}</span>
                </div>
                <small v-if="certStatus === 'unverified'" class="text-muted d-block mt-1">
                  Not found in the ERP yet. Run the client sync, then press Verify again.
                </small>
                <small v-if="certMessage" class="d-block mt-1 text-muted">{{ certMessage }}</small>
              </div>
            </div>

            <!-- Quotation Tab -->
            <div class="crm-tab-panel" v-if="activeTab === 'quotation'">
              <quotation-panel :card="card" @changed="$emit('quotation-changed', card)" />
            </div>

            <!-- Comments Tab -->
            <div class="crm-tab-panel" v-if="activeTab === 'comments'">
              <div class="crm-comment-form">
                <span class="crm-avatar crm-avatar--sm">{{ getInitials(currentUserName) }}</span>
                <div class="crm-comment-form__body">
                  <textarea
                    class="crm-textarea crm-textarea--sm"
                    v-model="newComment"
                    placeholder="Write a comment…"
                    rows="2"
                  ></textarea>
                  <button
                    class="crm-btn crm-btn--primary crm-btn--sm"
                    :disabled="!newComment.trim()"
                    @click="postComment"
                  >Post</button>
                </div>
              </div>
              <div class="crm-comments-list">
                <div
                  class="crm-comment"
                  v-for="(comment, i) in (card.comments || [])"
                  :key="i"
                >
                  <span class="crm-avatar crm-avatar--sm">{{ getInitials(comment.user) }}</span>
                  <div class="crm-comment__body">
                    <div class="crm-comment__header">
                      <strong class="crm-comment__user">{{ comment.user }}</strong>
                      <span class="crm-comment__time">{{ comment.time }}</span>
                    </div>
                    <p class="crm-comment__text">{{ comment.text }}</p>
                  </div>
                </div>
                <div class="crm-empty-state" v-if="!(card.comments || []).length">
                  <feather-icon icon="MessageSquareIcon" size="20" />
                  <span>No comments yet</span>
                </div>
              </div>
            </div>

            <!--
              MID-CRM P1.5 — the Checklist and Time Log panels were DELETED.
              crm_cards has no checklist or time-entry column and there is no
              crm_checklists / crm_time_entries table, so every tick and every
              logged hour was lost on refresh. The tabs were already hidden; the
              panels and their handlers are now gone too. Build the schema and the
              endpoints before reintroducing either.
            -->

            <!-- Activity Tab -->
            <div class="crm-tab-panel" v-if="activeTab === 'activity'">
              <!--
                Audit C-2: completed activities (calls / emails / visits) were being
                written to crm_card_activities on every activity and NEVER shown, so a
                rep could not see "we called them 3 times". Now loaded on demand.
              -->
              <div class="crm-drawer__subhead">
                <feather-icon icon="PhoneCallIcon" size="13" />
                <span>Contact history</span>
                <button class="crm-link-btn ml-auto" :disabled="loadingActivities" @click="fetchActivities">
                  {{ loadingActivities ? 'Loading…' : 'Refresh' }}
                </button>
              </div>

              <div class="crm-activity-timeline mb-1">
                <div
                  class="crm-activity-item"
                  v-for="(act, i) in activities"
                  :key="'act-' + i"
                >
                  <div class="crm-activity-item__dot crm-activity-item__dot--done"></div>
                  <div class="crm-activity-item__content">
                    <span class="crm-activity-item__action">
                      <strong>{{ act.type }}</strong>
                      <span v-if="act.note"> — {{ act.note }}</span>
                    </span>
                    <div class="crm-activity-item__meta">
                      <span>{{ act.user || '—' }}</span>
                      <span>{{ act.dateCompleted || act.date_completed || act.dueDate || act.due_date || '' }}</span>
                    </div>
                  </div>
                </div>

                <div class="crm-empty-state" v-if="!loadingActivities && !activities.length">
                  <feather-icon icon="PhoneIcon" size="20" />
                  <span>No calls, emails or visits logged yet</span>
                  <span class="text-xs text-muted">Complete an activity to build the contact history.</span>
                </div>
                <div class="crm-empty-state" v-if="activitiesError">
                  <feather-icon icon="AlertCircleIcon" size="20" />
                  <span>{{ activitiesError }}</span>
                </div>
              </div>

              <div class="crm-drawer__subhead">
                <feather-icon icon="ClockIcon" size="13" />
                <span>Change log</span>
              </div>
              <div class="crm-activity-timeline">
                <div
                  class="crm-activity-item"
                  v-for="(entry, i) in (card.history || [])"
                  :key="i"
                >
                  <div class="crm-activity-item__dot"></div>
                  <div class="crm-activity-item__content">
                    <span class="crm-activity-item__action">{{ entry.action }}</span>
                    <div class="crm-activity-item__meta">
                      <span>{{ entry.user }}</span>
                      <span>{{ entry.time }}</span>
                    </div>
                  </div>
                </div>
                <div class="crm-empty-state" v-if="!(card.history || []).length">
                  <feather-icon icon="ActivityIcon" size="20" />
                  <span>No changes recorded yet</span>
                </div>
              </div>
            </div>
          </div>

          <!--
            MID-CRM P1.4 / P1.6 — "Watchers" stays removed (still no storage
            behind it). Attachments is back below, this time real.
          -->
          <div class="crm-tab-panel" v-if="activeTab === 'files'">
            <div class="crm-drawer__section">
              <label class="crm-btn crm-btn--ghost crm-attach-upload-btn">
                <feather-icon icon="UploadIcon" size="14" />
                <span>{{ uploadingFile ? 'Uploading…' : 'Upload file' }}</span>
                <input type="file" class="d-none" :disabled="uploadingFile" @change="onAttachmentSelected" />
              </label>

              <div class="crm-activity-timeline mt-2">
                <div
                  v-for="file in (card.attachments || [])"
                  :key="file.id"
                  class="crm-activity-item"
                >
                  <div class="crm-activity-item__dot crm-activity-item__dot--done"></div>
                  <div class="crm-activity-item__content">
                    <a :href="file.url" target="_blank" rel="noopener" class="crm-activity-item__action">
                      <feather-icon icon="PaperclipIcon" size="12" class="mr-1" />
                      {{ file.name }}
                    </a>
                    <div class="crm-activity-item__meta">
                      <span>{{ file.uploadedBy || 'Unknown' }}</span>
                      <span>{{ formatFileSize(file.size) }}</span>
                      <button type="button" class="crm-inline-link text-danger" @click="removeAttachment(file)">Delete</button>
                    </div>
                  </div>
                </div>
                <div class="crm-empty-state" v-if="!(card.attachments || []).length">
                  <feather-icon icon="PaperclipIcon" size="20" />
                  <span>No files uploaded yet</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="crm-drawer__footer">
          <button class="crm-btn crm-btn--danger-outline" @click="$emit('delete', card)">
            <feather-icon icon="Trash2Icon" size="14" />
            Delete
          </button>
          <div class="crm-drawer__footer-right d-flex gap-2">
            <button class="crm-btn crm-btn--ghost" @click="printCard">
              <feather-icon icon="PrinterIcon" size="14" />
              Print Card
            </button>
            <button class="crm-btn crm-btn--ghost" @click="$emit('close')">Close</button>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
/* eslint-disable */
import axios from 'axios'
import flatPickr from 'vue-flatpickr-component'
import vSelect from 'vue-select'
import decryptData from '@/store/auth/decryptData'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import QuotationPanel from './QuotationPanel.vue'
import ClientPanel from './ClientPanel.vue'

export default {
  name: 'CardDetailDrawer',
  components: {
    flatPickr,
    vSelect,
    QuotationPanel,
    ClientPanel,
  },
  props: {
    card: {
      type: Object,
      required: true,
    },
    stages: {
      type: Array,
      required: true,
    },
    activePipeline: {
      type: String,
      required: true,
    },
    currentUserName: {
      type: String,
      default: 'User',
    },
    availableLabels: {
      type: Array,
      default: () => [],
    },
    /**
     * MID-CRM P1.2 — the REAL sales team from `GET crm/team`: [{ id, name }].
     * Replaces the old `teamMembers` prop, which was fed MOCK_TEAM_MEMBERS
     * (Mohammed, Sara, Khaled…) — names that matched no user in the database, so
     * nothing chosen from it could ever be saved.
     */
    team: {
      type: Array,
      default: () => [],
    },
    /** Only a manager may hand an opportunity to someone else (BRD §8.1). */
    canAssign: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      activeTab: 'client',
      // Inline editing states
      editingTitle: false,
      editTitle: '',
      editingClient: false,
      editClient: '',
      editingValue: false,
      editValue: 0,
      editingPriority: false,
      editingDesc: false,
      editDesc: '',
      // UI states
      showAssigneeSelect: false,
      showAddAssignee: false,
      assigneeToAdd: '',
      editingProduct: false,
      productSchemeEdit: '',
      productIdEdit: '',
      productPriceLoading: false,
      productCatalog: [],
      schemeOptions: [],
      showLabelSelect: false,
      // Form data
      newComment: '',
      // Audit C-2: completed activities loaded from the server
      activities: [],
      loadingActivities: false,
      renewingCert: { loading: false, data: null, error: null },
      activitiesError: null,
      // Audit C-1: certificate verification
      certNumber: '',
      certStatus: null,
      certMessage: '',
      savingCert: false,
      uploadingFile: false,
      dateConfig: {
        dateFormat: 'Y-m-d',
        altInput: true,
        altFormat: 'M j, Y',
        allowInput: true,
      },
    }
  },
  computed: {
    // Karim, 7 Sep: the primary owner is already shown above — this list is
    // everyone ELSE assigned (assigneesList always includes the owner too).
    otherAssignees() {
      return (this.card.assignees || []).filter(a => a.id !== this.card.ownerId)
    },
    productsForSchemeEdit() {
      if (!this.productSchemeEdit) return []
      return this.productCatalog.filter(p => p.crmName === this.productSchemeEdit)
    },
    // Audit C-1: report the ERP match result plainly.
    // Backend statuses are: matched | unverified | mismatch.
    certStatusText() {
      if (this.certStatus === 'matched') return 'Verified against the ERP — opportunity closed as Won'
      if (this.certStatus === 'unverified') return 'Saved, but NOT found in the ERP — not counted as Won'
      if (this.certStatus === 'mismatch') return 'This certificate belongs to a different client — flagged'
      return ''
    },
    certStatusClass() {
      return {
        'crm-cert-box__status--ok': this.certStatus === 'matched',
        'crm-cert-box__status--warn': this.certStatus === 'unverified',
        'crm-cert-box__status--bad': this.certStatus === 'mismatch',
      }
    },
    certStatusIcon() {
      if (this.certStatus === 'matched') return 'CheckCircleIcon'
      if (this.certStatus === 'mismatch') return 'XCircleIcon'
      return 'AlertTriangleIcon'
    },
    // MID-CRM P1.5 — checklistDoneCount / checklistPercent / totalHours removed with
    // the Checklist and Time Log features; they computed over card.checklist and
    // card.timeEntries, fields no endpoint has ever returned.
  },
  watch: {
    card: {
      immediate: true,
      handler(card) {
        // The drawer is always mounted and `card` is NULL until one is opened.
        // Without this guard the immediate watcher threw on mount
        // ("Cannot read properties of null reading 'certificateNumber'") and took
        // the ENTIRE pipeline page down — a blank board.
        if (!card) {
          this.activities = []
          this.activitiesError = null
          return
        }
        this.resetEditing()
        this.syncCertFromCard()
        this.activities = []
        this.activitiesError = null
        if (this.activeTab === 'activity') this.fetchActivities()
        this.fetchRenewingCert()
      },
    },
    activeTab(tab) {
      // Load the contact history only when the tab is actually opened.
      if (tab === 'activity' && !this.activities.length) this.fetchActivities()
    },
  },
  methods: {
    /** Karim, 2 Sep: show role + office next to each name when assigning. */
    memberLabel(member) {
      if (!member) return ''
      const roles = Array.isArray(member.roles) && member.roles.length ? member.roles.join('/') : ''
      const office = member.officeName || 'No office'
      return roles ? `${member.name} (${roles}) — ${office}` : `${member.name} — ${office}`
    },
    syncCertFromCard() {
      if (!this.card) return
      this.certNumber = this.card.certificateNumber || this.card.certificate_number || ''
      this.certStatus = this.card.certificateStatus || this.card.certificate_status || null
      this.certMessage = ''
    },

    /** First few values of a renewing-certificate list, "+N more" after; hover shows all. */
    renewingList(kind, max = 3) {
      const list = (this.renewingCert.data && this.renewingCert.data[kind]) || []
      if (list.length <= max) return list.join(', ')
      return `${list.slice(0, max).join(', ')} +${list.length - max} more`
    },
    shortText(text, max = 90) {
      if (!text) return ''
      return text.length > max ? `${text.slice(0, max)}…` : text
    },
    /** Same CEE request link the Contacts/Companies pages use (Karim, 21 Sep). */
    requestHref(id) {
      if (!id) return null
      const role = localStorage.getItem('accessRole') || ''
      const name = localStorage.getItem('name') || ''
      const email = localStorage.getItem('email') || ''
      const token = localStorage.getItem('accessToken') || ''
      return `https://cee.gulftic.com/orders/order/${id}?role=${role}&name=${name}&email=${email}&token=${token}`
    },
    /** Karim, 27 Sep: the ERP certificate this renewal card is for. */
    async fetchRenewingCert() {
      this.renewingCert = { loading: false, data: null, error: null }
      if (this.activePipeline !== 'renewals' || !this.card || !this.card.id) return
      const cardId = this.card.id
      this.renewingCert.loading = true
      try {
        const res = await axios.get(`crm/renewals/${cardId}/renewing-certificate`, { timeout: 20000 })
        if (!this.card || this.card.id !== cardId) return
        const body = decryptData(res.data.payload)
        this.renewingCert = { loading: false, data: (body && body.data) || null, error: null }
      } catch (e) {
        if (!this.card || this.card.id !== cardId) return
        this.renewingCert = { loading: false, data: null, error: 'Could not load the certificate details.' }
      }
    },

    /** Audit C-2: the completed calls/emails/visits for this opportunity. */
    async fetchActivities() {
      if (!this.card || !this.card.id) return
      this.loadingActivities = true
      this.activitiesError = null
      try {
        const res = await axios.get(`crm/${this.activePipeline}/${this.card.id}/activities`, { timeout: 20000 })
        const body = decryptData(res.data.payload)
        this.activities = (body && Array.isArray(body.data)) ? body.data : []
      } catch (e) {
        this.activitiesError = 'Could not load the contact history.'
        console.error('fetchActivities failed:', e)
      } finally {
        this.loadingActivities = false
      }
    },

    /**
     * Audit C-1: send the certificate number for ERP verification.
     * The server decides the outcome — we only report it. We never set the card
     * to Won locally, because an unmatched number must not look like a win.
     */
    async linkCertificate() {
      const number = (this.certNumber || '').trim()
      if (!number) return
      this.savingCert = true
      this.certMessage = ''
      try {
        const res = await axios.post(
          `crm/${this.activePipeline}/${this.card.id}/certificate`,
          { certificateNumber: number },
          { timeout: 20000 }
        )
        const body = decryptData(res.data.payload)
        // Server returns { card, matched, hint|certificate }
        const data = (body && body.data) || {}
        const updatedCard = data.card || {}
        this.certStatus = updatedCard.certificateStatus || 'unverified'
        this.certMessage = data.hint || (body && body.message) || ''
        // Let the board re-sync from the server (stage may now be Won/Renewed).
        this.$emit('certificate-linked', { cardId: this.card.id, card: updatedCard, status: this.certStatus })
      } catch (e) {
        this.certMessage = 'The server rejected the certificate number. Please retry.'
        console.error('linkCertificate failed:', e)
      } finally {
        this.savingCert = false
      }
    },

    // Karim, 7 Sep: real opportunity attachments.
    async onAttachmentSelected(e) {
      const file = e.target.files && e.target.files[0]
      e.target.value = ''
      if (!file) return
      this.uploadingFile = true
      const form = new FormData()
      form.append('file', file)
      try {
        const res = await axios.post(
          `crm/${this.activePipeline}/${this.card.id}/attachments`,
          form,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        )
        const body = decryptData(res.data.payload)
        const updatedCard = (body && body.data) || {}
        this.$emit('card-refreshed', { cardId: this.card.id, card: updatedCard })
      } catch (err) {
        this.$toast({
          component: ToastificationContent,
          props: { title: 'Upload failed', icon: 'AlertCircleIcon', variant: 'danger', text: 'The file could not be uploaded.' },
        })
      } finally {
        this.uploadingFile = false
      }
    },
    async removeAttachment(file) {
      try {
        const res = await axios.delete(`crm/${this.activePipeline}/${this.card.id}/attachments/${file.id}`)
        const body = decryptData(res.data.payload)
        const updatedCard = (body && body.data) || {}
        this.$emit('card-refreshed', { cardId: this.card.id, card: updatedCard })
      } catch (err) {
        this.$toast({
          component: ToastificationContent,
          props: { title: 'Failed', icon: 'AlertCircleIcon', variant: 'danger', text: 'Could not remove the file.' },
        })
      }
    },
    formatFileSize(bytes) {
      if (!bytes) return ''
      if (bytes < 1024) return `${bytes} B`
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    },

    resetEditing() {
      this.editingTitle = false
      this.editingClient = false
      this.editingValue = false
      this.editingPriority = false
      this.editingDesc = false
      this.showAssigneeSelect = false
      this.showLabelSelect = false
      this.activeTab = 'client'
      this.newComment = ''
    },
    getInitials(name) {
      if (!name) return '?'
      return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    },
    formatCurrency(val) {
      if (val == null) return 'AED 0'
      return 'AED ' + Number(val).toLocaleString()
    },
    priorityColor(p) {
      const map = { High: '#f43f5e', Medium: '#2563eb', Low: '#10b981' }
      return map[p] || '#94a3b8'
    },

    // Inline editing
    startEditTitle() {
      this.editTitle = this.card.title
      this.editingTitle = true
      this.$nextTick(() => {
        if (this.$refs.titleInput) this.$refs.titleInput.focus()
      })
    },
    saveTitle() {
      if (this.editTitle.trim() && this.editTitle !== this.card.title) {
        this.$emit('field-update', { cardId: this.card.id, field: 'title', value: this.editTitle.trim() })
      }
      this.editingTitle = false
    },
    startEdit(field) {
      if (field === 'client') {
        this.editClient = this.card.client || ''
        this.editingClient = true
        this.$nextTick(() => { if (this.$refs.clientInput) this.$refs.clientInput.focus() })
      } else if (field === 'value') {
        this.editValue = this.card.value || 0
        this.editingValue = true
        this.$nextTick(() => { if (this.$refs.valueInput) this.$refs.valueInput.focus() })
      }
    },
    saveField(field, value) {
      this.$emit('field-update', { cardId: this.card.id, field, value })
      if (field === 'client') this.editingClient = false
      if (field === 'value') this.editingValue = false
    },
    startEditDesc() {
      this.editDesc = this.card.description || ''
      this.editingDesc = true
      this.$nextTick(() => { if (this.$refs.descTextarea) this.$refs.descTextarea.focus() })
    },
    saveDesc() {
      this.$emit('field-update', { cardId: this.card.id, field: 'description', value: this.editDesc })
      this.editingDesc = false
    },
    onDueDateChange(selectedDates, dateStr) {
      this.$emit('field-update', { cardId: this.card.id, field: 'dueDate', value: dateStr || null })
    },

    // Assignees
    /**
     * MID-CRM P2.1 — hand the opportunity to a real salesperson.
     * Emits owner + ownerId together so the card carries both the id (what the
     * server scopes on) and the name (what the board displays).
     */
    setOwner(userId) {
      const user = this.team.find(u => String(u.id) === String(userId))
      if (!user) return
      this.$emit('owner-change', { cardId: this.card.id, userId: user.id, userName: user.name })
      this.showAssigneeSelect = false
    },

    // Karim, 7 Sep: multi-assignee, fully equal — additive on top of owner.
    addAssignee() {
      const user = this.team.find(u => String(u.id) === String(this.assigneeToAdd))
      if (!user) return
      this.$emit('assignee-add', { cardId: this.card.id, userId: user.id, userName: user.name })
      this.showAddAssignee = false
      this.assigneeToAdd = ''
    },
    removeAssignee(assignee) {
      this.$emit('assignee-remove', { cardId: this.card.id, userId: assignee.id })
    },

    // Karim, 8 Sep: product select on an EXISTING opportunity — same catalog
    // CardAddModal uses, simplified (no per-line tier picker) for a quick edit.
    async startEditProduct() {
      this.productSchemeEdit = this.card.serviceScheme || ''
      this.productIdEdit = this.card.productId || ''
      this.editingProduct = true
      if (!this.productCatalog.length) {
        try {
          const res = await axios.get('crm/products')
          const body = decryptData(res.data.payload)
          this.productCatalog = (body && body.data && body.data.products) || []
          this.schemeOptions = [...new Set(this.productCatalog.map(p => p.crmName).filter(Boolean))].sort()
        } catch (e) { /* optional */ }
      }
    },
    onProductSchemeEditChange() {
      this.productIdEdit = ''
    },
    async applyProductEdit() {
      const scheme = this.productSchemeEdit
      if (!scheme) return
      this.productPriceLoading = true
      let value = this.card.value
      let currency = this.card.currency
      try {
        const params = { scheme }
        if (this.productIdEdit) params.productId = this.productIdEdit
        const res = await axios.get('crm/products/price', { params })
        const body = decryptData(res.data.payload)
        const priceInfo = (body && body.data) || null
        if (priceInfo && !priceInfo.isProvisional) {
          value = (priceInfo.lines || []).reduce((sum, l) => sum + (l.tiered ? 0 : (l.amount || 0)), 0)
          if (priceInfo.currency) currency = priceInfo.currency
        }
      } catch (e) { /* keep existing value if the price lookup fails */ }
      this.productPriceLoading = false
      this.editingProduct = false

      this.$emit('product-update', {
        cardId: this.card.id,
        serviceScheme: scheme,
        productId: this.productIdEdit || null,
        value,
        currency,
      })
    },

    // Labels
    onLabelSelect(label) {
      if (!label) return
      this.$emit('label-add', { cardId: this.card.id, label })
      this.showLabelSelect = false
    },

    // Comments
    postComment() {
      if (!this.newComment.trim()) return
      this.$emit('comment-add', { cardId: this.card.id, text: this.newComment.trim() })
      this.newComment = ''
    },

    // MID-CRM P1.5/P1.6 — removed: addCheckItem(), addTimeEntry(), addAttachment(),
    // removeAttachment(), downloadAttachment().
    // None of them could store anything: `crm_cards` has no checklist, time-entry,
    // attachment or watcher column, and there is no crm_checklists / crm_time_entries
    // / crm_attachments table. The attachment methods were pure theatre — a hardcoded
    // "1.5 MB" size and a download that only did alert("Downloading …").
    // Do not restore any of them without building the schema + endpoint first.

    printCard() {
      window.print()
    }
  },
}
</script>

<style lang="scss" scoped>
$primary: #2563eb;
$primary-dark: #1d4ed8;
$success: #10b981;
$warning: #2563eb;
$danger: #f43f5e;
$slate-50: #f8fafc;
$slate-100: #f1f5f9;
$slate-200: #e2e8f0;
$slate-300: #cbd5e1;
$slate-400: #94a3b8;
$slate-500: #64748b;
$slate-600: #475569;
$slate-700: #334155;
$slate-900: #0f172a;

// ── Drawer Backdrop ──
.crm-drawer-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1060;
  background: rgba(15, 23, 42, 0.35);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}

// ── Drawer Panel ──
.crm-drawer {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 480px;
  max-width: 100vw;
  background: #fff;
  display: flex;
  flex-direction: column;
  box-shadow: -12px 0 40px rgba(0, 0, 0, 0.1), -1px 0 0 $slate-100;

  &__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 20px 24px 16px;
    border-bottom: 1px solid $slate-100;
    flex-shrink: 0;
  }

  &__header-left {
    flex: 1;
    min-width: 0;
  }

  &__id {
    display: inline-block;
    font-family: 'SFMono-Regular', 'Consolas', monospace;
    font-size: 11px;
    font-weight: 600;
    color: $primary;
    background: rgba($primary, 0.08);
    padding: 3px 8px;
    border-radius: 6px;
    margin-bottom: 6px;
  }

  &__title-wrap {
    margin-top: 4px;
  }

  &__title {
    font-family: 'Outfit', 'Inter', sans-serif;
    font-size: 18px;
    font-weight: 700;
    color: $slate-900;
    margin: 0;
    cursor: pointer;
    padding: 2px 4px;
    margin: -2px -4px;
    border-radius: 6px;
    transition: background 0.15s;
    line-height: 1.35;

    &:hover {
      background: $slate-50;
    }
  }

  &__close {
    width: 34px;
    height: 34px;
    border-radius: 8px;
    border: none;
    background: $slate-50;
    color: $slate-400;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    flex-shrink: 0;
    margin-left: 12px;

    &:hover {
      background: $slate-100;
      color: $slate-600;
    }
  }

  &__body {
    flex: 1;
    overflow-y: auto;
    padding: 0;
  }

  &__section {
    padding: 18px 24px;
    border-bottom: 1px solid $slate-100;
  }

  &__section-label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-family: 'Outfit', 'Inter', sans-serif;
    font-size: 11.5px;
    font-weight: 700;
    color: $slate-400;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin-bottom: 10px;
  }

  &__tabs-section {
    padding: 0;
    border-bottom: none;
  }

  &__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 24px;
    border-top: 1px solid $slate-100;
    flex-shrink: 0;
  }

  &__footer-right {
    display: flex;
    gap: 8px;
  }
}

// ── Info Grid ──
.crm-info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
  padding: 4px 24px 18px !important;

  &__item {
    padding: 10px 0;
    border-bottom: 1px solid $slate-50;

    &:nth-child(odd) { padding-right: 12px; }
    &:nth-child(even) { padding-left: 12px; }
  }

  &__label {
    display: block;
    font-size: 11px;
    font-weight: 600;
    color: $slate-400;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 4px;
  }

  &__static {
    font-size: 13.5px;
    color: $slate-700;
    font-weight: 500;
  }
}

.crm-renew-cert {
  padding: 0 !important;
  &__link { color: #2563eb; text-decoration: none; display: inline-flex; align-items: center; gap: 4px; }
  &__link:hover { text-decoration: underline; }
}

// ── Inline Edit ──
.crm-inline-edit {
  &__value {
    display: inline-block;
    font-size: 13.5px;
    color: $slate-700;
    font-weight: 500;
    padding: 4px 8px;
    margin: -4px -8px;
    border-radius: 6px;
    border: 1.5px solid transparent;
    cursor: pointer;
    transition: all 0.15s;
    min-width: 40px;

    &:hover {
      border-color: $slate-200;
      background: $slate-50;
    }

    &--money {
      font-weight: 600;
      color: $success;
    }
  }

  &__input {
    font-family: 'Outfit', 'Inter', sans-serif;
    width: 100%;
    border: 1.5px solid $primary;
    border-radius: 8px;
    padding: 6px 10px;
    font-size: 13.5px;
    color: $slate-700;
    background: #fff;
    outline: none;
    box-shadow: 0 0 0 3px rgba($primary, 0.08);
    box-sizing: border-box;

    &--title {
      font-size: 18px;
      font-weight: 700;
      padding: 4px 8px;
    }

    &--date {
      border-color: $slate-200;
      box-shadow: none;
      cursor: pointer;

      &:focus {
        border-color: $primary;
        box-shadow: 0 0 0 3px rgba($primary, 0.08);
      }
    }
  }
}

.crm-priority-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 5px;
  vertical-align: middle;
}

.crm-text-danger {
  color: $danger !important;
  font-weight: 600 !important;
}

// Karim, 7 Sep: attachments + assignees.
.crm-inline-link {
  border: none; background: none; padding: 0; font-size: 11px; font-weight: 600;
  cursor: pointer; text-decoration: underline;
}
.crm-attach-upload-btn { cursor: pointer; display: inline-flex; }
.crm-assignee-chip {
  display: inline-flex; align-items: center; gap: 5px;
  background: #f1f5f9; border: 1px solid #e2e8f0; border-radius: 999px;
  padding: 3px 8px 3px 10px; font-size: 12px; font-weight: 600; color: #334155;

  &__remove {
    border: none; background: none; color: #94a3b8; cursor: pointer;
    display: flex; align-items: center; padding: 1px;
    &:hover { color: $danger; }
  }
}

// ── Select ──
.crm-select {
  font-family: 'Outfit', 'Inter', sans-serif;
  width: 100%;
  border: 1.5px solid $slate-200;
  border-radius: 10px;
  padding: 9px 14px;
  font-size: 13.5px;
  color: $slate-700;
  background: #fff;
  outline: none;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%2394a3b8' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 14px center;
  transition: border-color 0.2s, box-shadow 0.2s;
  box-sizing: border-box;

  &:focus {
    border-color: $primary;
    box-shadow: 0 0 0 3px rgba($primary, 0.1);
  }
}

// ── Assignees ──
.crm-assignees {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.crm-assignee {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px 4px 4px;
  background: $slate-50;
  border-radius: 20px;
  border: 1px solid $slate-200;

  &__avatar {
    width: 26px;
    height: 26px;
    border-radius: 50%;
    background: linear-gradient(135deg, $primary, $primary-dark);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    font-weight: 700;
  }

  &__name {
    font-size: 12.5px;
    font-weight: 500;
    color: $slate-700;
  }

  &__remove {
    width: 18px;
    height: 18px;
    border: none;
    background: transparent;
    color: $slate-400;
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.15s;

    &:hover {
      background: rgba($danger, 0.1);
      color: $danger;
    }
  }
}

.crm-assignee-select {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 180px;
}

// ── Labels ──
.crm-labels {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
}

.crm-label-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 11.5px;
  font-weight: 600;
  border: 1px solid;

  &__remove {
    width: 16px;
    height: 16px;
    border: none;
    background: transparent;
    color: inherit;
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0.6;
    transition: opacity 0.15s;

    &:hover {
      opacity: 1;
    }
  }
}

.crm-label-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 6px;
  vertical-align: middle;
}

.crm-label-select {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 180px;
}

// ── Link Button ──
.crm-link-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  color: $primary;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
  transition: background 0.15s;

  &:hover {
    background: rgba($primary, 0.06);
  }
}

.crm-icon-btn {
  width: 28px;
  height: 28px;
  border: none;
  background: $slate-50;
  color: $slate-400;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
  flex-shrink: 0;

  &:hover {
    background: $slate-100;
    color: $slate-600;
  }
}

// ── Description ──
.crm-desc-display {
  padding: 10px 14px;
  background: $slate-50;
  border-radius: 10px;
  border: 1.5px solid transparent;
  cursor: pointer;
  transition: all 0.15s;
  min-height: 50px;

  p {
    margin: 0;
    font-size: 13.5px;
    color: $slate-700;
    line-height: 1.6;
    white-space: pre-wrap;
  }

  &__placeholder {
    color: $slate-400 !important;
    font-style: italic;
  }

  &:hover {
    border-color: $slate-200;
    background: #fff;
  }
}

.crm-desc-edit {
  &__actions {
    display: flex;
    gap: 8px;
    margin-top: 10px;
  }
}

// ── Tabs ──
.crm-tabs {
  display: flex;
  border-bottom: 1px solid $slate-100;
  padding: 0 24px;
  gap: 0;
}

.crm-tab {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 12px 14px;
  font-family: 'Outfit', 'Inter', sans-serif;
  font-size: 12.5px;
  font-weight: 600;
  color: $slate-400;
  border: none;
  background: none;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  transition: all 0.2s;

  &:hover {
    color: $slate-600;
  }

  &--active {
    color: $primary;
    border-bottom-color: $primary;
  }

  &__count {
    font-size: 10.5px;
    padding: 1px 6px;
    border-radius: 10px;
    background: $slate-100;
    color: $slate-500;
  }

  &--active &__count {
    background: rgba($primary, 0.1);
    color: $primary;
  }
}

.crm-tab-panel {
  padding: 18px 24px;
}

// ── Comments ──
.crm-comment-form {
  display: flex;
  gap: 10px;
  margin-bottom: 18px;

  &__body {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: flex-end;
  }
}

.crm-avatar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: linear-gradient(135deg, $primary, $primary-dark);
  color: #fff;
  font-weight: 700;

  &--sm {
    width: 30px;
    height: 30px;
    font-size: 10.5px;
  }
}

.crm-comments-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.crm-comment {
  display: flex;
  gap: 10px;

  &__body {
    flex: 1;
    background: $slate-50;
    border-radius: 10px;
    padding: 10px 14px;
  }

  &__header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;
  }

  &__user {
    font-size: 12.5px;
    color: $slate-700;
  }

  &__time {
    font-size: 11px;
    color: $slate-400;
  }

  &__text {
    margin: 0;
    font-size: 13px;
    color: $slate-600;
    line-height: 1.5;
  }
}

// ── Checklist ──
.crm-checklist-progress {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;

  &__bar {
    flex: 1;
    height: 6px;
    background: $slate-100;
    border-radius: 6px;
    overflow: hidden;
  }

  &__fill {
    height: 100%;
    background: $primary;
    border-radius: 6px;
    transition: width 0.35s ease;

    &--complete {
      background: $success;
    }
  }

  &__text {
    font-size: 12px;
    font-weight: 700;
    color: $slate-500;
    min-width: 32px;
    text-align: right;
  }
}

.crm-checklist-add {
  display: flex;
  gap: 8px;
  margin-bottom: 14px;
}

.crm-checklist-items {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.crm-check-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 8px;
  transition: background 0.15s;

  &:hover {
    background: $slate-50;
  }

  &--done &__text {
    text-decoration: line-through;
    color: $slate-400;
  }

  &__checkbox {
    position: relative;
    cursor: pointer;

    input { display: none; }
  }

  &__checkmark {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border-radius: 5px;
    border: 2px solid $slate-300;
    color: transparent;
    transition: all 0.2s;
  }

  &__checkbox input:checked + &__checkmark {
    background: $primary;
    border-color: $primary;
    color: #fff;
  }

  &__text {
    flex: 1;
    font-size: 13px;
    color: $slate-700;
    transition: all 0.2s;
  }

  &__delete {
    width: 22px;
    height: 22px;
    border: none;
    background: transparent;
    color: $slate-300;
    border-radius: 4px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: all 0.15s;
  }

  &:hover &__delete {
    opacity: 1;
  }

  &__delete:hover {
    color: $danger;
    background: rgba($danger, 0.08);
  }
}

// ── Time Log ──
.crm-timelog-total {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: rgba($primary, 0.04);
  border-radius: 10px;
  margin-bottom: 14px;
  font-size: 13.5px;
  color: $slate-600;

  strong {
    color: $primary;
  }
}

.crm-timelog-form {
  display: flex;
  gap: 8px;
  margin-bottom: 14px;

  &__hours {
    width: 70px;
    flex-shrink: 0;
  }

  &__note {
    flex: 1;
  }
}

.crm-timelog-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.crm-timelog-entry {
  display: flex;
  gap: 12px;
  padding: 10px 12px;
  background: $slate-50;
  border-radius: 8px;

  &__hours {
    font-size: 14px;
    font-weight: 700;
    color: $primary;
    min-width: 36px;
  }

  &__details {
    flex: 1;
  }

  &__note {
    display: block;
    font-size: 13px;
    color: $slate-700;
  }

  &__meta {
    display: block;
    font-size: 11px;
    color: $slate-400;
    margin-top: 2px;
  }
}

// ── Activity ──
.crm-activity-timeline {
  position: relative;
  padding-left: 20px;

  &::before {
    content: '';
    position: absolute;
    left: 5px;
    top: 8px;
    bottom: 8px;
    width: 2px;
    background: $slate-100;
    border-radius: 2px;
  }
}

.crm-activity-item {
  display: flex;
  gap: 12px;
  padding: 8px 0;
  position: relative;

  &__dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: $primary;
    border: 2px solid #fff;
    box-shadow: 0 0 0 2px rgba($primary, 0.2);
    flex-shrink: 0;
    margin-top: 4px;
    margin-left: -17px;
  }

  &__content {
    flex: 1;
  }

  &__action {
    display: block;
    font-size: 13px;
    color: $slate-700;
    line-height: 1.4;
  }

  &__meta {
    display: flex;
    gap: 8px;
    font-size: 11px;
    color: $slate-400;
    margin-top: 2px;

    span + span::before {
      content: '·';
      margin-right: 8px;
    }
  }
}

// ── Watchers ──
.crm-watchers {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
}

.crm-watcher-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: $slate-50;
  border: 1px solid $slate-200;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  color: $slate-600;

  &__remove {
    width: 16px;
    height: 16px;
    border: none;
    background: transparent;
    color: $slate-400;
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.15s;

    &:hover {
      color: $danger;
    }
  }
}

// ── Empty State ──
.crm-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 24px 0;
  color: $slate-300;
  font-size: 12.5px;
}

// ── Audit C-2 / C-1: activity sub-headings + certificate box ──
.crm-drawer__subhead {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: $slate-400;
  margin: 10px 0 6px;
}

.crm-activity-item__dot--done {
  background-color: #10b981 !important;
}

.crm-cert-box {
  margin-top: 14px;
  padding: 12px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #f8fafc;

  &__row {
    display: flex;
    gap: 8px;
    align-items: center;

    .crm-input {
      flex: 1;
    }
  }

  &__status {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 8px;
    font-size: 12px;
    font-weight: 700;

    &--ok { color: #10b981; }
    &--warn { color: #2563eb; }
    &--bad { color: #ef4444; }
  }
}

// ── Shared Form Elements ──
.crm-input {
  font-family: 'Outfit', 'Inter', sans-serif;
  border: 1.5px solid $slate-200;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 13.5px;
  color: $slate-700;
  background: #fff;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
  box-sizing: border-box;

  &:focus {
    border-color: $primary;
    box-shadow: 0 0 0 3px rgba($primary, 0.1);
  }

  &--sm {
    padding: 7px 10px;
    font-size: 13px;
  }
}

.crm-textarea {
  font-family: 'Outfit', 'Inter', sans-serif;
  width: 100%;
  border: 1.5px solid $slate-200;
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 13.5px;
  color: $slate-700;
  background: #fff;
  outline: none;
  resize: vertical;
  min-height: 60px;
  transition: border-color 0.2s, box-shadow 0.2s;
  box-sizing: border-box;

  &:focus {
    border-color: $primary;
    box-shadow: 0 0 0 3px rgba($primary, 0.1);
  }

  &::placeholder {
    color: $slate-400;
  }

  &--sm {
    min-height: 48px;
    font-size: 13px;
    padding: 8px 12px;
  }
}

.crm-vselect {
  flex: 1;

  ::v-deep .vs__dropdown-toggle {
    border: 1.5px solid $slate-200;
    border-radius: 8px;
    padding: 4px 8px;
    min-height: 34px;
  }

  ::v-deep .vs__search {
    font-family: 'Outfit', 'Inter', sans-serif;
    font-size: 13px;
  }

  ::v-deep .vs__dropdown-menu {
    border-radius: 8px;
    border: 1.5px solid $slate-200;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
    padding: 4px;
  }

  ::v-deep .vs__dropdown-option {
    border-radius: 6px;
    padding: 6px 10px;
    font-size: 13px;
  }

  ::v-deep .vs__dropdown-option--highlight {
    background: rgba($primary, 0.08);
    color: $primary-dark;
  }

  &--sm ::v-deep .vs__dropdown-toggle {
    min-height: 30px;
  }
}

// ── Buttons ──
.crm-btn {
  font-family: 'Outfit', 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 600;
  padding: 8px 16px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 5px;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  &--primary {
    background: linear-gradient(135deg, $primary, $primary-dark);
    color: #fff;
    box-shadow: 0 2px 8px rgba($primary, 0.25);

    &:hover:not(:disabled) {
      box-shadow: 0 4px 12px rgba($primary, 0.35);
      transform: translateY(-1px);
    }
  }

  &--ghost {
    background: $slate-50;
    color: $slate-600;
    border: 1px solid $slate-200;

    &:hover {
      background: $slate-100;
    }
  }

  &--outline {
    background: #fff;
    color: $slate-500;
    border: 1.5px solid $slate-200;

    &:hover:not(:disabled) {
      border-color: $primary;
      color: $primary;
    }
  }

  &--danger-outline {
    background: transparent;
    color: $danger;
    border: 1.5px solid rgba($danger, 0.3);

    &:hover {
      background: rgba($danger, 0.04);
      border-color: $danger;
    }
  }

  &--sm {
    padding: 6px 12px;
    font-size: 12.5px;
  }
}

// ── Drawer Slide Transition ──
.crm-drawer-slide-enter-active,
.crm-drawer-slide-leave-active {
  transition: opacity 0.3s ease;

  .crm-drawer {
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
}

.crm-drawer-slide-enter,
.crm-drawer-slide-leave-to {
  opacity: 0;

  .crm-drawer {
    transform: translateX(100%);
  }
}
</style>
